/*
 * A food, a recipe or a meal written into an address so it can be handed to somebody else —
 * the nutrition spec, Sharing.
 *
 * The whole share travels in the link. Nothing is kept on the server, which is what lets a
 * link go on working after the account that wrote it has changed the meal, deleted it or
 * stopped using the app at all; the cost is that the address has to hold everything, so what
 * follows is compact rather than pretty. It is JSON, deflated and written in base64url — a
 * hundred-member meal comes to under a thousand characters that way, where the plain JSON
 * would be four thousand.
 *
 * This is the one shape both apps write and both apps read, so the spec pins it and the
 * Android port in `data/Share.kt` decodes the same corpus. A field added here without being
 * added there is a link one app writes and the other refuses.
 */

/** The version this app writes. A link saying anything else is not one it can read. */
export const SHARE_VERSION = 1

/**
 * The most a link may come to, characters and all. Unreachable by a real meal — a hundred
 * members of foods nobody else has comes to under a thousand — so it is a guard rail against a
 * shape nobody thought of, and never a limit somebody meets while using the app.
 */
export const SHARE_LIMIT = 8000

/** The path a shared link is served at, under whichever origin wrote it. */
export const SHARE_PATH = '/shared'

/**
 * The units an amount can be taken in, as the link writes them. The same vocabulary the apps
 * store, so a link says what was typed rather than what it came to: `[1, 'cup']`, never
 * `[240, 'ml']`.
 */
export const SHARE_UNITS = [
  'g',
  'oz',
  'lb',
  'ml',
  'tsp',
  'tbsp',
  'fl oz',
  'cup',
  'portion',
] as const

export type ShareUnit = (typeof SHARE_UNITS)[number]
export type ShareReference = '100g' | '100ml' | 'portion'

/** `[value, unit]`, and a third place naming the portion where the unit is one. */
export type ShareAmount = readonly [number, ShareUnit] | readonly [number, ShareUnit, string]

/**
 * The eight nutrients in the order the spec defines them — calories, protein, carbohydrates,
 * fat, fibre, sugar, saturated fat, sodium — with null for one not stated. The unstated tail
 * is left off entirely, which is most of the length of a meal of ordinary foods.
 */
export type ShareNutrients = readonly (number | null)[]

/** `[name, amount, unit]`. */
export type SharePortion = readonly [string, number, 'g' | 'ml']

/** A food written out whole, for one nobody but its owner is offered. */
export interface ShareCopy {
  readonly n: string
  readonly rf: ShareReference
  /** Per the reference amount, the way a food states its own. */
  readonly nu: ShareNutrients
  readonly e?: string
  readonly b?: string
  readonly bc?: string
  readonly p?: readonly SharePortion[]
  /** Grams per millilitre, on a liquid. */
  readonly dn?: number
}

/**
 * One thing in a share, in the amount it is offered in. Exactly one of `r`, `c` and `n`
 * stands: a reference to a food everybody is offered, a copy of one they are not, or a name
 * and its nutrients, which is a quick entry and has no food behind it at all.
 */
export interface ShareMember {
  readonly a: ShareAmount
  /** `l:` and a library food's code. */
  readonly r?: string
  readonly c?: ShareCopy
  readonly n?: string
  /** What this much of the quick entry comes to, in total rather than per anything. */
  readonly nu?: ShareNutrients
}

export interface SharedMeal {
  readonly v: typeof SHARE_VERSION
  readonly k: 'meal'
  readonly by?: string
  readonly n: string
  readonly e?: string
  readonly m: readonly ShareMember[]
}

export interface SharedRecipe {
  readonly v: typeof SHARE_VERSION
  readonly k: 'recipe'
  readonly by?: string
  readonly n: string
  readonly e?: string
  readonly sv: number
  /** What the whole recipe weighs in grams, where whoever wrote it said. */
  readonly tw?: number
  readonly i: readonly ShareMember[]
}

export interface SharedFood {
  readonly v: typeof SHARE_VERSION
  readonly k: 'food'
  readonly by?: string
  /**
   * What it goes by. A library food travels as a reference and so carries no name of its own
   * anywhere else in the link; saying it here is what lets the link describe itself without
   * asking the server first — the chat card, and the heading of a page still reading its
   * references.
   */
  readonly n: string
  readonly e?: string
  readonly f: ShareMember
}

/**
 * Entries somebody is handed to log — the nutrition spec, Sending and sharing. Not a
 * blueprint: there is nothing here to keep, only a moment and what was eaten at it, so it
 * carries the day and the time as well as the members.
 */
export interface SharedEntry {
  readonly v: typeof SHARE_VERSION
  readonly k: 'entry'
  readonly by?: string
  readonly n: string
  readonly e?: string
  /** The day it lands on, `YYYY-MM-DD`. */
  readonly dy: string
  /** The time it lands at, `HH:MM`. */
  readonly tm: string
  readonly m: readonly ShareMember[]
}

export type Shared = SharedMeal | SharedRecipe | SharedFood | SharedEntry

/** Every reference a share carries, in the order it carries them. */
export function referencesOf(shared: Shared): string[] {
  return membersOf(shared)
    .map((member) => member.r)
    .filter((reference): reference is string => reference !== undefined)
}

/** The members a share holds, whichever kind it is. */
export function membersOf(shared: Shared): readonly ShareMember[] {
  switch (shared.k) {
    case 'meal':
    case 'entry':
      return shared.m
    case 'recipe':
      return shared.i
    case 'food':
      return [shared.f]
  }
}

/**
 * Whether what was handed over is entries to log rather than a blueprint to keep — the one
 * question every page opening a link asks first, since it decides which key leads.
 */
export function isEntryShare(shared: Shared): shared is SharedEntry {
  return shared.k === 'entry'
}

/** What it goes by. Every kind says it, so nothing has to be read to know it. */
export function nameOf(shared: Shared): string {
  return shared.n
}

/** The emoji it wears, or null where it wears none. */
export function emojiOf(shared: Shared): string | null {
  return shared.e ?? null
}

const NUTRIENT_PLACES = 8

/**
 * Nutrients as a share writes them: the eight in order, the unstated tail dropped. Calories
 * are never dropped — a food that states none states zero, and an empty array would be a food
 * saying nothing at all.
 */
export function shareNutrients(values: readonly (number | null | undefined)[]): ShareNutrients {
  const places: (number | null)[] = []
  for (let index = 0; index < NUTRIENT_PLACES; index++) {
    const value = values[index]
    places.push(value === undefined || Number.isNaN(value) ? null : value)
  }
  while (places.length > 1 && places[places.length - 1] === null) {
    places.pop()
  }
  places[0] = places[0] ?? 0
  return places
}

/** And back: the eight places, whatever the link left off. */
export function nutrientPlaces(nutrients: ShareNutrients): (number | null)[] {
  const places: (number | null)[] = []
  for (let index = 0; index < NUTRIENT_PLACES; index++) {
    places.push(nutrients[index] ?? null)
  }
  places[0] = places[0] ?? 0
  return places
}

/* ---- The wire. ---- */

function toBase64Url(bytes: Uint8Array): string {
  let binary = ''
  for (const byte of bytes) {
    binary += String.fromCharCode(byte)
  }
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '')
}

function fromBase64Url(text: string): Uint8Array<ArrayBuffer> | null {
  const padded = text.replaceAll('-', '+').replaceAll('_', '/')
  try {
    const binary = atob(padded.padEnd(Math.ceil(padded.length / 4) * 4, '='))
    const bytes = new Uint8Array(binary.length)
    for (let index = 0; index < binary.length; index++) {
      bytes[index] = binary.charCodeAt(index)
    }
    return bytes
  } catch {
    return null
  }
}

/**
 * Bytes through a compression stream and out the other side.
 *
 * Written on the reader and the writer rather than on `Blob.stream()` and `Response`, which
 * is the shorter way to say it: jsdom carries the compression streams but not `Blob.stream`,
 * so the short way runs in a browser and in the Worker and fails in the tests. The write is
 * deliberately not awaited — a stream applying backpressure will not let it settle until
 * something is reading, and the read is on the far side of it.
 */
async function through(
  stream: CompressionStream | DecompressionStream,
  input: Uint8Array<ArrayBuffer>,
): Promise<Uint8Array> {
  const writer = stream.writable.getWriter()
  /*
   * Swallowed rather than merely not awaited: bytes that are not deflated at all fail the
   * stream from both ends, and an unhandled rejection over here would be reported as a crash
   * even though the read below has already answered for it.
   */
  const swallow = () => undefined
  writer.write(input).catch(swallow)
  writer.close().catch(swallow)
  const reader = stream.readable.getReader() as ReadableStreamDefaultReader<Uint8Array>
  const chunks: Uint8Array[] = []
  let length = 0
  for (;;) {
    const { done, value } = await reader.read()
    if (done) {
      break
    }
    chunks.push(value)
    length += value.length
  }
  const whole = new Uint8Array(length)
  let at = 0
  for (const chunk of chunks) {
    whole.set(chunk, at)
    at += chunk.length
  }
  return whole
}

async function deflate(text: string): Promise<Uint8Array> {
  return through(new CompressionStream('deflate-raw'), new TextEncoder().encode(text))
}

async function inflate(bytes: Uint8Array<ArrayBuffer>): Promise<string | null> {
  try {
    return new TextDecoder().decode(await through(new DecompressionStream('deflate-raw'), bytes))
  } catch {
    return null
  }
}

/** What a share comes to as the `d` parameter of a link. */
export async function encodeShare(shared: Shared): Promise<string> {
  return toBase64Url(await deflate(JSON.stringify(shared)))
}

/**
 * A whole link, origin and all. The origin is passed in rather than read from anywhere: the
 * apps have different ones, and a link written on Android has to name the web app's.
 */
export async function shareLink(origin: string, shared: Shared): Promise<string> {
  return `${origin.replace(/\/$/, '')}${SHARE_PATH}?d=${await encodeShare(shared)}`
}

/**
 * What a link carries, or null where it carries nothing this app can read — damaged, or
 * written by a later version. The two are one answer on purpose: the spec says a link that
 * cannot be read says so, and says the same thing either way.
 */
export async function decodeShare(parameter: string): Promise<Shared | null> {
  const bytes = fromBase64Url(parameter)
  if (bytes === null) {
    return null
  }
  const text = await inflate(bytes)
  if (text === null) {
    return null
  }
  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch {
    return null
  }
  return sharedOf(parsed)
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function stringOf(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined
}

function nutrientsOf(value: unknown): ShareNutrients | null {
  if (!Array.isArray(value) || value.length === 0) {
    return null
  }
  const places: (number | null)[] = []
  for (const each of value) {
    if (each === null) {
      places.push(null)
    } else if (typeof each === 'number' && Number.isFinite(each)) {
      places.push(each)
    } else {
      return null
    }
  }
  return places
}

function amountOf(value: unknown): ShareAmount | null {
  if (!Array.isArray(value) || value.length < 2) {
    return null
  }
  const [magnitude, unit, portion] = value as unknown[]
  if (typeof magnitude !== 'number' || !Number.isFinite(magnitude)) {
    return null
  }
  if (!SHARE_UNITS.includes(unit as ShareUnit)) {
    return null
  }
  const said = unit as ShareUnit
  return typeof portion === 'string' ? [magnitude, said, portion] : [magnitude, said]
}

function portionsOf(value: unknown): readonly SharePortion[] | undefined {
  if (!Array.isArray(value)) {
    return undefined
  }
  const portions: SharePortion[] = []
  for (const each of value) {
    if (!Array.isArray(each) || each.length < 3) {
      continue
    }
    const [name, amount, unit] = each as unknown[]
    if (typeof name === 'string' && typeof amount === 'number' && (unit === 'g' || unit === 'ml')) {
      portions.push([name, amount, unit])
    }
  }
  return portions.length === 0 ? undefined : portions
}

function copyOf(value: unknown): ShareCopy | null {
  if (!isObject(value)) {
    return null
  }
  const name = stringOf(value.n)
  const reference = value.rf
  const nutrients = nutrientsOf(value.nu)
  if (
    name === undefined ||
    nutrients === null ||
    (reference !== '100g' && reference !== '100ml' && reference !== 'portion')
  ) {
    return null
  }
  const portions = portionsOf(value.p)
  const emoji = stringOf(value.e)
  const brand = stringOf(value.b)
  const barcode = stringOf(value.bc)
  const density = weightOf(value.dn)
  return {
    n: name,
    rf: reference,
    nu: nutrients,
    ...(emoji === undefined ? {} : { e: emoji }),
    ...(brand === undefined ? {} : { b: brand }),
    ...(barcode === undefined ? {} : { bc: barcode }),
    ...(portions === undefined ? {} : { p: portions }),
    ...(density === undefined ? {} : { dn: density }),
  }
}

/** A weight — a number above nothing — or unsaid where the field is anything else. */
function weightOf(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined
}

function memberOf(value: unknown): ShareMember | null {
  if (!isObject(value)) {
    return null
  }
  const amount = amountOf(value.a)
  if (amount === null) {
    return null
  }
  const reference = stringOf(value.r)
  if (reference !== undefined) {
    return reference.startsWith('l:') ? { a: amount, r: reference } : null
  }
  if (value.c !== undefined) {
    const copy = copyOf(value.c)
    return copy === null ? null : { a: amount, c: copy }
  }
  const name = stringOf(value.n)
  const nutrients = nutrientsOf(value.nu)
  if (name === undefined || nutrients === null) {
    return null
  }
  return { a: amount, n: name, nu: nutrients }
}

function membersFrom(value: unknown): ShareMember[] | null {
  if (!Array.isArray(value) || value.length === 0) {
    return null
  }
  const members: ShareMember[] = []
  for (const each of value) {
    const member = memberOf(each)
    if (member === null) {
      return null
    }
    members.push(member)
  }
  return members
}

/** A share as this app understands one, or null where the object is not one. */
export function sharedOf(value: unknown): Shared | null {
  if (!isObject(value) || value.v !== SHARE_VERSION) {
    return null
  }
  const by = stringOf(value.by)
  const carriedBy = by === undefined ? {} : { by }
  const name = stringOf(value.n)
  const emoji = stringOf(value.e)
  const wearing = emoji === undefined ? {} : { e: emoji }
  if (value.k === 'meal') {
    const members = membersFrom(value.m)
    if (name === undefined || members === null) {
      return null
    }
    return { v: SHARE_VERSION, k: 'meal', ...carriedBy, n: name, ...wearing, m: members }
  }
  if (value.k === 'recipe') {
    const ingredients = membersFrom(value.i)
    const servings = value.sv
    if (
      name === undefined ||
      ingredients === null ||
      typeof servings !== 'number' ||
      !Number.isFinite(servings) ||
      servings <= 0
    ) {
      return null
    }
    // The weight travels where there is one, so a recipe shared with somebody arrives able to
    // be logged by weight the way it was where it was written.
    const weighs = value.tw
    const weighing =
      typeof weighs === 'number' && Number.isFinite(weighs) && weighs > 0 ? { tw: weighs } : {}
    return {
      v: SHARE_VERSION,
      k: 'recipe',
      ...carriedBy,
      n: name,
      ...wearing,
      sv: servings,
      ...weighing,
      i: ingredients,
    }
  }
  if (value.k === 'entry') {
    const members = membersFrom(value.m)
    const day = dayOf(value.dy)
    const time = timeOf(value.tm)
    if (name === undefined || members === null || day === null || time === null) {
      return null
    }
    return {
      v: SHARE_VERSION,
      k: 'entry',
      ...carriedBy,
      n: name,
      ...wearing,
      dy: day,
      tm: time,
      m: members,
    }
  }
  if (value.k === 'food') {
    const member = memberOf(value.f)
    if (member === null || name === undefined) {
      return null
    }
    return { v: SHARE_VERSION, k: 'food', ...carriedBy, n: name, ...wearing, f: member }
  }
  /*
   * A kind this app does not know is a link a later one wrote, and is refused the way a later
   * version is — the nutrition spec, The link. Which is why a kind added to the format never
   * moves the version: an older app already has the right answer for it.
   */
  return null
}

/** A day as a link writes one, or null where the field is anything else. */
function dayOf(value: unknown): string | null {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null
}

/** A time as a link writes one, or null where the field is anything else. */
function timeOf(value: unknown): string | null {
  return typeof value === 'string' && /^\d{2}:\d{2}$/.test(value) ? value : null
}
